import { useState } from 'react'
import cx from 'classnames'

import ReactCodeInput from 'react-verification-code-input'
import { FormErrorMessage } from 'common/FormErrorMessage/FormErrorMessage'
import { GhostButton } from 'components/Buttons/GhostButton'
import { ReactComponent as LeftArrowIcon } from 'assets/icons/arrowLeft24.svg'

import Styles from './CheckEmailOrEnterVerificationCode.module.scss'
import Shared from '../../Shared.module.scss'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UserClient } from 'api/clients/UserClient'

import { trackVerifyWithCodeComplete, trackVerifyWithCodeFailed } from 'screens/LoginOrSignup/utils'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Button } from 'blaze-ui/Button'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import ImageAssembly from '../Blaze/Testimonials/CartoonTestimonials/ImageAssembly'
import ProgressBar from '../ProgressBar/ProgressBar'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'

type Props = {
  email: string
  totalSteps: number
  transitionPage: (page: string) => void
  setEmailVerificationCode: (code: string) => void
  workspaceInviteToken?: string
}

const CheckEmailOrEnterVerificationCode = (props: Props) => {
  const { email, transitionPage, setEmailVerificationCode, totalSteps, workspaceInviteToken } = props
  const [clearInput, setClearInput] = useState(0)
  const [error, setError] = useState('')
  const [codeDisabled, setCodeDisabled] = useState(false)

  const { isTabletPortrait, isMobile } = useResponsiveDesign()

  const resendEmail = async () => {
    const res = await UserClient.sendEmail(email, 'email_verification', undefined, workspaceInviteToken)
    if (res.type === SUCCESS_RESPONSE_TYPE) {
      showToastMessage('Email sent!', 'success')
    } else {
      showToastMessage('Something went wrong. Please try again later', 'error')
    }
  }

  const checkVerificationCode = async (code: string) => {
    setCodeDisabled(true)

    const resp = await UserClient.verifyEmailConfirmationCode(email, code)

    if (resp.type === SUCCESS_RESPONSE_TYPE) {
      setEmailVerificationCode(code)
      trackVerifyWithCodeComplete()
      transitionPage('CREATE_ACCOUNT')
      return
    }

    // maximum number of attempts have been reached
    resp.type === 'error' && (resp.status === 403 || resp.data.errors[0].verify_with_code_attempts >= 3)
      ? setError('Maximum code attempts have been exceeded. Verify your email by clicking the link we sent to you')
      : setError('The code you entered does not match our records')

    setCodeDisabled(false)
    trackVerifyWithCodeFailed(error)
    setClearInput(clearInput ? 0 : 1)
  }

  const clearErrors = () => {
    setError('')
  }

  return (
    <div data-testid="checkEmailOrEnterVerificationCode" className={cx(Shared.content, Shared.isBlaze)}>
      <div
        className={cx(Shared.leftPanel, {
          [Styles.imgBackgroundFull]: !isMobile,
          [Shared.imgBackgroundFull]: isMobile,
        })}
      >
        <div className={Shared.header}>
          <GhostButton
            className={cx(Shared.backButton, Shared.isBlaze)}
            aria-label="Back"
            onClick={() => transitionPage('GO_BACK')}
          >
            <LeftArrowIcon />
          </GhostButton>
          <ProgressBar totalSteps={totalSteps} currentStep={1} />
        </div>
        <div className={Styles.leftContent}>
          {isMobile && (
            <div
              className={Shared.tinyHero}
              style={{
                backgroundImage: `url(https://res.cloudinary.com/almanac/image/upload/v1715632986/blaze_assets/backgrounds/first_time_experience/TinyHero_mmsm0i.png)`,
              }}
            />
          )}
          <div className={cx(Shared.leftContent, Styles.leftContent)}>
            <div className={cx(Shared.title, Styles.title)}>
              <Heading level={isMobile ? 3 : 1}>Verify your email</Heading>
            </div>
            <div className={Styles.mainContent}>
              <div className={Styles.messageText}>
                <Text variant="secondary" color="var(--dark-60)">
                  Enter the code we sent to {email} to finish signing up. Check your{' '}
                  <span className={Styles.bold}>spam inbox</span> in case you don't see it.
                </Text>
              </div>
              <CodeInput
                disabled={codeDisabled}
                clearInput={clearInput}
                onChange={clearErrors}
                onComplete={checkVerificationCode}
              />

              {error && (
                <div className={Styles.errorContainer}>
                  <FormErrorMessage>{error}</FormErrorMessage>
                </div>
              )}
              <div className={Styles.tosContainer}>
                <div className={Styles.buttonContainer}>
                  {isMobile ? (
                    <div onClick={resendEmail} className={Shared.link}>
                      Resend Email
                    </div>
                  ) : (
                    <Button variant="tertiary" ignoreHover size="lg" onPress={resendEmail} type="button">
                      Resend Email
                    </Button>
                  )}
                </div>
                {isMobile && (
                  <div className={Styles.termsOfServiceAndPrivacyPolicy}>
                    <span>
                      <Text variant="label">
                        By continuing, you agree to Blaze's{' '}
                        <a
                          tabIndex={0}
                          href="https://www.blaze.ai/terms"
                          target="_blank"
                          rel="noreferrer"
                          className={Shared.smallLink}
                        >
                          Terms of Service.
                        </a>
                      </Text>
                      <Text variant="label">
                        Read our{' '}
                        <a
                          href="https://www.blaze.ai/privacy-policy"
                          target="_blank"
                          rel="noreferrer"
                          className={Shared.smallLink}
                        >
                          Privacy Policy.
                        </a>
                      </Text>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {!isTabletPortrait && !isMobile && (
        <ImageAssembly offsetTop={230} showSmallPerson useSmallerBubble page={'checkYourInboxForPasswordResetEmail'} />
      )}
    </div>
  )
}

/*
 * CodeInput
 * - clearInput is a hack to re-render the ReactCodeInput component with new default values
 * - ReactCodeInput does not support clearing the input fields
 */
const CodeInput = ({ disabled, clearInput, onChange, onComplete }: any) => {
  return (
    <div className={Styles.codeWrapper} key={clearInput}>
      <ReactCodeInput
        disabled={disabled}
        className={Styles.verificationCode}
        type="number"
        fields={4}
        fieldHeight={64}
        onComplete={onComplete}
        onChange={onChange}
        values={Array(4).fill('')}
        data-heap-id="email-verification-code"
      />
    </div>
  )
}

export default CheckEmailOrEnterVerificationCode
